import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { HeartHandshake, Tag, Gift, Layers, Calendar, MapPin, ArrowRight, Loader2, Zap, Eye, Mail } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const usd = (c) => `$${((c || 0) / 100).toFixed(0)}`;

const TIERS = [
  { icon: Tag, name: 'Logo Soup', price: 'from $99', accent: 'text-emerald-500', blurb: 'Your logo + link on the event page and in event marketing emails. Activates instantly on payment.', badge: 'Instant' },
  { icon: Gift, name: 'Swag Bag Offer', price: 'from $249', accent: 'text-fuchsia-500', blurb: 'Put an exclusive offer in every attendee\'s digital swag bag — trackable clicks and redemptions.', badge: 'High intent' },
  { icon: Layers, name: 'Custom Tiers', price: 'varies', accent: 'text-indigo-500', blurb: 'Stage naming, booth space, MC shout-outs — organizers build custom packages per event.', badge: 'Premium' },
];

function fmtDate(iso) {
  if (!iso) return '';
  try {
    return new Date(String(iso).slice(0, 10) + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return String(iso).slice(0, 10); }
}

export default function SponsorsPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/sponsorship/open`)
      .then(res => setEvents(res.data.events || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-background pt-20 pb-16" data-testid="sponsors-page">
      <Helmet>
        <title>Sponsor an Event | Eventism</title>
        <meta name="description" content="Put your brand in front of engaged local audiences. Sponsor events on Eventism — logo placements, swag bag offers and custom packages." />
        <link rel="canonical" href="https://eventism.com/sponsors" />
      </Helmet>

      <section className="max-w-6xl mx-auto px-4 pt-6 pb-12">
        <Badge className="mb-4 bg-indigo-500/15 text-indigo-500 border border-indigo-500/30 hover:bg-indigo-500/15">
          <HeartHandshake className="h-3 w-3 mr-1" /> For brands
        </Badge>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight max-w-3xl">
          Put your brand where the <span className="bg-gradient-to-r from-fuchsia-500 via-indigo-500 to-amber-500 bg-clip-text text-transparent">crowd already is</span>
        </h1>
        <p className="mt-4 text-base text-muted-foreground max-w-2xl">
          Sponsor real local events — wine tastings, comedy nights, NYE parties — and reach attendees who actually show up. Self-serve checkout, instant activation, transparent pricing.
        </p>
        <div className="mt-6 flex flex-wrap gap-5 text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5"><Zap className="h-3.5 w-3.5 text-amber-500" /> Instant activation</span>
          <span className="flex items-center gap-1.5"><Eye className="h-3.5 w-3.5 text-fuchsia-500" /> Trackable views &amp; clicks</span>
          <span className="flex items-center gap-1.5"><Tag className="h-3.5 w-3.5 text-emerald-500" /> From $99 per event</span>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 pb-14">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {TIERS.map((t) => (
            <Card key={t.name} className="border-border" data-testid={`sponsor-tier-${t.name.toLowerCase().replace(/\s/g, '-')}`}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <t.icon className={`h-5 w-5 ${t.accent}`} />
                  <Badge variant="outline" className="text-[10px]">{t.badge}</Badge>
                </div>
                <div className="font-semibold">{t.name}</div>
                <div className={`text-sm font-medium ${t.accent}`}>{t.price}</div>
                <p className="text-xs text-muted-foreground mt-2 leading-relaxed">{t.blurb}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4">
        <h2 className="text-lg font-semibold mb-1">Events open for sponsorship</h2>
        <p className="text-sm text-muted-foreground mb-5">Pick an event, choose your tier, check out in minutes.</p>
        {loading ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : events.length === 0 ? (
          <Card className="border-dashed" data-testid="sponsors-empty-state">
            <CardContent className="py-12 text-center">
              <HeartHandshake className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
              <p className="font-medium">No events are open for sponsorship right now</p>
              <p className="text-sm text-muted-foreground mt-1">Want to sponsor something specific? Tell us what you're looking for.</p>
              <Link to="/contact">
                <Button className="mt-5 rounded-full bg-gradient-to-r from-fuchsia-500 to-indigo-500 text-white hover:opacity-90" data-testid="sponsors-contact-cta">
                  <Mail className="mr-1.5 h-4 w-4" /> Contact Us
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="sponsors-events-grid">
            {events.map((ev) => (
              <Link key={ev.event_id} to={`/sponsor/${ev.event_id}`} data-testid={`sponsor-event-${ev.event_id}`} className="group">
                <Card className="border-border overflow-hidden h-full hover:border-fuchsia-500/50 transition-colors">
                  {ev.image_url && (
                    <div className="h-36 overflow-hidden">
                      <img src={ev.image_url} alt={ev.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                  )}
                  <CardContent className="p-4">
                    <div className="font-semibold text-sm line-clamp-2">{ev.name}</div>
                    <div className="mt-2 space-y-1 text-xs text-muted-foreground">
                      {ev.event_date && <div className="flex items-center gap-1.5"><Calendar className="h-3 w-3" /> {fmtDate(ev.event_date)}</div>}
                      {(ev.venue_name || ev.city) && <div className="flex items-center gap-1.5"><MapPin className="h-3 w-3" /> {[ev.venue_name, ev.city].filter(Boolean).join(' · ')}</div>}
                    </div>
                    <div className="mt-3 flex items-center justify-between">
                      {ev.from_price_cents ? (
                        <Badge className="bg-emerald-500/15 text-emerald-500 border border-emerald-500/30 hover:bg-emerald-500/15">from {usd(ev.from_price_cents)}</Badge>
                      ) : <span />}
                      <span className="text-xs font-medium text-fuchsia-500 flex items-center gap-1">Sponsor <ArrowRight className="h-3 w-3 group-hover:translate-x-1 transition-transform" /></span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </section>

      <section className="max-w-6xl mx-auto px-4 mt-14">
        <Card className="border-border bg-gradient-to-br from-fuchsia-500/10 via-indigo-500/10 to-amber-500/10">
          <CardContent className="p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h3 className="font-semibold text-lg">Looking for a bigger play?</h3>
              <p className="text-sm text-muted-foreground mt-1">Season-long partnerships, venue takeovers, multi-event bundles — we'll build it with you.</p>
            </div>
            <Link to="/contact">
              <Button className="rounded-full bg-gradient-to-r from-fuchsia-500 to-indigo-500 text-white hover:opacity-90 whitespace-nowrap" data-testid="sponsors-custom-cta">
                Talk to us <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      </section>
    </div>
  );
}
